import React, { useState } from 'react';
import { Alert } from '@trussworks/react-uswds';

const EDUCATION_OPTIONS = [
  { value: '', label: 'Select Education Level' },
  { value: 8, label: '8th Grade or Less' },
  { value: 9, label: '9th Grade' },
  { value: 10, label: '10th Grade' },
  { value: 11, label: '11th Grade' },
  { value: 12, label: 'High School Graduate or GED' },
  { value: 13, label: 'Some College' },
  { value: 14, label: 'Associate Degree' },
  { value: 15, label: 'Bachelor\'s Degree' },
  { value: 16, label: 'Master\'s Degree' },
  { value: 17, label: 'Doctorate or Professional Degree' }
];

// Helper function to convert numeric education code to readable label
export const getEducationLabel = (value) => {
  const option = EDUCATION_OPTIONS.find(opt => String(opt.value) === String(value));
  return option ? option.label : 'Not provided';
};

const MaritalHistory = ({ formData, updateFormData, nextStep, prevStep }) => {
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  const handlePartyAChange = (field, value) => {
    setShowValidationError(false);
    updateFormData({
      partyA: {
        ...formData.partyA,
        maritalHistory: {
          ...formData.partyA?.maritalHistory,
          [field]: value
        }
      }
    });
  };

  const handlePartyBChange = (field, value) => {
    setShowValidationError(false);
    updateFormData({
      partyB: {
        ...formData.partyB,
        maritalHistory: {
          ...formData.partyB?.maritalHistory,
          [field]: value
        }
      }
    });
  };

  const handleNext = () => {
    // Validate Party A fields
    const partyAPreviousMarriages = formData.partyA?.maritalHistory?.previousMarriages;
    const partyAEducation = formData.partyA?.maritalHistory?.education;

    // Validate Party B fields
    const partyBPreviousMarriages = formData.partyB?.maritalHistory?.previousMarriages;
    const partyBEducation = formData.partyB?.maritalHistory?.education;

    // Check if required fields are filled
    if (!partyAPreviousMarriages && partyAPreviousMarriages !== '0') {
      setValidationMessage('Please enter the number of previous marriages for Party A.');
      setShowValidationError(true);
      return;
    }

    if (!partyAEducation) {
      setValidationMessage('Please select the highest education completed for Party A.');
      setShowValidationError(true);
      return;
    }

    if (!partyBPreviousMarriages && partyBPreviousMarriages !== '0') {
      setValidationMessage('Please enter the number of previous marriages for Party B.');
      setShowValidationError(true);
      return;
    }

    if (!partyBEducation) {
      setValidationMessage('Please select the highest education completed for Party B.');
      setShowValidationError(true);
      return;
    }

    // All validation passed
    nextStep();
  };

  return (
    <div>
      <button onClick={prevStep} className="btn btn-secondary question-back-button">
        Back to Previous Question
      </button>

      <h1 className="form-section-title">Marital History</h1>

      {/* Party A Marital History */}
      <div className="form-section">
        <h2 className="form-subsection-title">
          Party A - {formData.partyA?.firstName} {formData.partyA?.lastName}
        </h2>

        <div className="form-grid cols-2">
          <div className="form-field">
            <label className="form-label" htmlFor="partyA-previousMarriages">
              Number of Previous Marriages <span className="required">*</span>
            </label>
            <input
              type="number"
              id="partyA-previousMarriages"
              className="form-input"
              min="0"
              max="99"
              value={formData.partyA?.maritalHistory?.previousMarriages || ''}
              onChange={(e) => handlePartyAChange('previousMarriages', e.target.value)}
              required
            />
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="partyA-lastMarriageEndDate">
              Date Last Marriage Ended
            </label>
            <input
              type="date"
              id="partyA-lastMarriageEndDate"
              className="form-input"
              value={formData.partyA?.maritalHistory?.lastMarriageEndDate || ''}
              onChange={(e) => handlePartyAChange('lastMarriageEndDate', e.target.value)}
              disabled={!formData.partyA?.maritalHistory?.previousMarriages || parseInt(formData.partyA?.maritalHistory?.previousMarriages) < 1}
            />
          </div>
        </div>

        <div className="form-field">
          <label className="form-label">Reason Last Marriage Ended</label>
          <div className="button-group">
            <button
              type="button"
              className={`btn ${formData.partyA?.maritalHistory?.lastMarriageEndReason === 'Divorce' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handlePartyAChange('lastMarriageEndReason', 'Divorce')}
              disabled={!formData.partyA?.maritalHistory?.previousMarriages || parseInt(formData.partyA?.maritalHistory?.previousMarriages) < 1}
            >
              Divorce
            </button>
            <button
              type="button"
              className={`btn ${formData.partyA?.maritalHistory?.lastMarriageEndReason === 'Death' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handlePartyAChange('lastMarriageEndReason', 'Death')}
              disabled={!formData.partyA?.maritalHistory?.previousMarriages || parseInt(formData.partyA?.maritalHistory?.previousMarriages) < 1}
            >
              Death
            </button>
            <button
              type="button"
              className={`btn ${formData.partyA?.maritalHistory?.lastMarriageEndReason === 'Annulment' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handlePartyAChange('lastMarriageEndReason', 'Annulment')}
              disabled={!formData.partyA?.maritalHistory?.previousMarriages || parseInt(formData.partyA?.maritalHistory?.previousMarriages) < 1}
            >
              Annulment
            </button>
          </div>
        </div>

        <div className="form-grid cols-1">
          <div className="form-field">
            <label className="form-label" htmlFor="partyA-education">
              Highest Education Completed <span className="required">*</span>
            </label>
            <select
              id="partyA-education"
              className="form-input"
              value={formData.partyA?.maritalHistory?.education || ''}
              onChange={(e) => handlePartyAChange('education', e.target.value)}
              required
            >
              {EDUCATION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Party B Marital History */}
      <div className="form-section">
        <h2 className="form-subsection-title">
          Party B - {formData.partyB?.firstName} {formData.partyB?.lastName}
        </h2>

        <div className="form-grid cols-2">
          <div className="form-field">
            <label className="form-label" htmlFor="partyB-previousMarriages">
              Number of Previous Marriages <span className="required">*</span>
            </label>
            <input
              type="number"
              id="partyB-previousMarriages"
              className="form-input"
              min="0"
              max="99"
              value={formData.partyB?.maritalHistory?.previousMarriages || ''}
              onChange={(e) => handlePartyBChange('previousMarriages', e.target.value)}
              required
            />
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="partyB-lastMarriageEndDate">
              Date Last Marriage Ended
            </label>
            <input
              type="date"
              id="partyB-lastMarriageEndDate"
              className="form-input"
              value={formData.partyB?.maritalHistory?.lastMarriageEndDate || ''}
              onChange={(e) => handlePartyBChange('lastMarriageEndDate', e.target.value)}
              disabled={!formData.partyB?.maritalHistory?.previousMarriages || parseInt(formData.partyB?.maritalHistory?.previousMarriages) < 1}
            />
          </div>
        </div>

        <div className="form-field">
          <label className="form-label">Reason Last Marriage Ended</label>
          <div className="button-group">
            <button
              type="button"
              className={`btn ${formData.partyB?.maritalHistory?.lastMarriageEndReason === 'Divorce' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handlePartyBChange('lastMarriageEndReason', 'Divorce')}
              disabled={!formData.partyB?.maritalHistory?.previousMarriages || parseInt(formData.partyB?.maritalHistory?.previousMarriages) < 1}
            >
              Divorce
            </button>
            <button
              type="button"
              className={`btn ${formData.partyB?.maritalHistory?.lastMarriageEndReason === 'Death' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handlePartyBChange('lastMarriageEndReason', 'Death')}
              disabled={!formData.partyB?.maritalHistory?.previousMarriages || parseInt(formData.partyB?.maritalHistory?.previousMarriages) < 1}
            >
              Death
            </button>
            <button
              type="button"
              className={`btn ${formData.partyB?.maritalHistory?.lastMarriageEndReason === 'Annulment' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handlePartyBChange('lastMarriageEndReason', 'Annulment')}
              disabled={!formData.partyB?.maritalHistory?.previousMarriages || parseInt(formData.partyB?.maritalHistory?.previousMarriages) < 1}
            >
              Annulment
            </button>
          </div>
        </div>

        <div className="form-grid cols-1">
          <div className="form-field">
            <label className="form-label" htmlFor="partyB-education">
              Highest Education Completed <span className="required">*</span>
            </label>
            <select
              id="partyB-education"
              className="form-input"
              value={formData.partyB?.maritalHistory?.education || ''}
              onChange={(e) => handlePartyBChange('education', e.target.value)}
              required
            >
              {EDUCATION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {showValidationError && (
        <Alert type="error" headingLevel="h4" slim className="margin-top-2">
          {validationMessage}
        </Alert>
      )}

      <div className="step-navigation">
        <button onClick={prevStep} className="btn btn-secondary">Back</button>
        <button onClick={handleNext} className="btn btn-primary">Next</button>
      </div>
    </div>
  );
};

export default MaritalHistory;
